import { memo, useEffect, useMemo, useState } from 'react'
import * as THREE from 'three'

interface GroundShadowProps {
  width: number
  depth: number
  position?: [number, number, number]
  opacity?: number
  blur?: number // Reserved for future use
}

/**
 * GroundShadow component creates a soft, gradient shadow on the ground
 * Similar to a CSS box-shadow effect, positioned at the base of furniture
 */
export const GroundShadow = memo(function GroundShadow({
  width,
  depth,
  position = [0, 0.01, 70],
  opacity = 0.15,
  blur,
}: GroundShadowProps) {
  void blur
  // Both texture and material are owned GPU resources. Build them in an
  // effect and dispose in cleanup so StrictMode's discarded first mount
  // doesn't leak a CanvasTexture / MeshBasicMaterial.
  const [material, setMaterial] = useState<THREE.MeshBasicMaterial | null>(
    null
  )

  useEffect(() => {
    const aspectRatio = Math.max(width, depth) / Math.min(width, depth)
    const baseSize = 512
    const canvasWidth = width > depth ? baseSize * aspectRatio : baseSize
    const canvasHeight = depth > width ? baseSize * aspectRatio : baseSize

    const canvas = document.createElement('canvas')
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    const context = canvas.getContext('2d')

    let texture: THREE.CanvasTexture | null = null
    let nextMaterial: THREE.MeshBasicMaterial

    if (!context) {
      nextMaterial = new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: opacity * 0.3,
      })
    } else {
      context.clearRect(0, 0, canvasWidth, canvasHeight)

      const centerX = canvasWidth / 2
      const centerY = canvasHeight / 2
      const radiusX = canvasWidth / 2
      const radiusY = canvasHeight / 2
      const maxRadius = Math.max(radiusX, radiusY)

      const gradient = context.createRadialGradient(
        centerX,
        centerY,
        maxRadius * 0.05,
        centerX,
        centerY,
        maxRadius * 0.95
      )

      gradient.addColorStop(0, `rgba(0, 0, 0, ${opacity * 0.9})`)
      gradient.addColorStop(0.15, `rgba(0, 0, 0, ${opacity * 0.7})`)
      gradient.addColorStop(0.3, `rgba(0, 0, 0, ${opacity * 0.5})`)
      gradient.addColorStop(0.5, `rgba(0, 0, 0, ${opacity * 0.3})`)
      gradient.addColorStop(0.7, `rgba(0, 0, 0, ${opacity * 0.15})`)
      gradient.addColorStop(0.85, `rgba(0, 0, 0, ${opacity * 0.05})`)
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')

      context.fillStyle = gradient
      context.fillRect(0, 0, canvasWidth, canvasHeight)

      texture = new THREE.CanvasTexture(canvas)
      texture.wrapS = THREE.ClampToEdgeWrapping
      texture.wrapT = THREE.ClampToEdgeWrapping
      texture.needsUpdate = true

      nextMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 1,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
    }

    setMaterial(nextMaterial)

    return () => {
      texture?.dispose()
      nextMaterial.dispose()
    }
  }, [opacity, width, depth])

  const shadowDimensions = useMemo(
    () => ({ width: width * 1.3, depth: depth * 1.3 }),
    [width, depth]
  )

  if (!material) return null

  return (
    <mesh
      position={position}
      rotation={[-Math.PI / 2, 0, 0]}
      material={material}
      receiveShadow={false}
      castShadow={false}
      visible={false}
      userData={{ isGroundShadow: true }}
    >
      <planeGeometry args={[shadowDimensions.width, shadowDimensions.depth]} />
    </mesh>
  )
})

GroundShadow.displayName = 'GroundShadow'
